import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Text from 'core-system/Text'
import { AppState } from 'redux/config/store'
import { autoEnrollActions } from './autoEnrollSlice'
import { tagsActions } from 'redux/tags/tagsSlice'
import { AutoEnrollRule } from './autoEnrollTypes'
import AddAutoEnrollRuleForm from './AddAutoEnrollRuleForm'
import AutoEnrollRuleList from './components/AutoEnrollRuleList'
import SyncSettingsCollapsible from 'features/Segments/syncSettings/SyncSettingsCollapsible'

/**
 * Auto-enroll section for Sync Settings.
 * Rules and actions come from Redux (autoEnroll slice).
 */
const AutoEnrollSection: React.FC = () => {
  const dispatch = useDispatch()
  const rules = useSelector((state: AppState) => state.autoEnroll.rules)

  useEffect(() => {
    dispatch(autoEnrollActions.getAutoEnrollRules())
    dispatch(tagsActions.getTags())
  }, [dispatch])

  const handleAddRule = useCallback(
    (rule: Omit<AutoEnrollRule, 'id'>) => {
      dispatch(autoEnrollActions.addAutoEnrollRule(rule))
    },
    [dispatch]
  )

  const handleDeleteRule = useCallback(
    (id: string) => {
      dispatch(autoEnrollActions.removeAutoEnrollRule(id))
    },
    [dispatch]
  )

  const handleUpdateRule = useCallback(
    (id: string, changes: Partial<Omit<AutoEnrollRule, 'id'>>) => {
      dispatch(autoEnrollActions.updateAutoEnrollRule({ id, changes }))
    },
    [dispatch]
  )

  return (
    <SyncSettingsCollapsible
      title='Auto-enroll rules'
      status={`${rules.length} rule${rules.length === 1 ? '' : 's'}`}
    >
      <Text variant='body2' color='grey600' marginBottom='1rem'>
        Rules are evaluated after HRIS sync, onboarding, and worksite changes.
        Use AND when every check must match, or OR when any check is enough.
      </Text>
      <AddAutoEnrollRuleForm onAddRule={handleAddRule} />
      <AutoEnrollRuleList
        rules={rules}
        onDeleteRule={handleDeleteRule}
        onUpdateRule={handleUpdateRule}
      />
    </SyncSettingsCollapsible>
  )
}

export default React.memo(AutoEnrollSection)
